import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData, getChartData } from '../../hooks/useDashboardData';
import { useResponsive } from '../../hooks/useResponsive';
import { 
  useDiningGroupMonthlyTrendQuery, 
  useDiningOptionsMonthlyTrendQuery
} from '../../hooks/queries/useDashboardQuery';
import './DiningGroupChart.css';
import '../charts/ChartStyles.css';


interface DiningGroup {
  dining_group: string;
  net_sales: string;
  order_count: string;
  percentage: string;
}

interface DiningOption {
  cleaned_dining_option: string;
  net_sales: string;
  order_count: string;
  percentage: string;
  recalculated_percentage?: number; 
}

interface MonthlyDiningGroupData {
  year_month: string;
  groups: {
    [key: string]: {
      dining_group: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
    }
  }
}

interface MonthlyDiningOptionsData {
  year_month: string;
  options: {
    [key: string]: {
      cleaned_dining_option: string;
      gross_sales: string;
      total_refunds: string;
      net_sales: string;
      order_count: number;
    }
  }
}

interface MonthlyDiningOptionsResponse {
  activeOptions: string[];
  monthlyData: MonthlyDiningOptionsData[];
}

interface ModalPosition {
  y: number;
}

interface MainDiningChartProps {
  dashboardData: any;
  selectedGroup: string | null;
  onGroupSelect: (group: string | null) => void;
  onMonthlyTrendClick: (event: React.MouseEvent) => void;
  isFiltersApplying: boolean;
  filters: {
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
  };
}

interface MonthlyTrendModalProps {
  show: boolean;
  onClose: () => void;
  salesforceId: string | null;
  modalPosition: ModalPosition;
  
}


const extractNumber = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }
  return value || 0;
};

const extractPercentage = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace('%', ''));
  }
  return value || 0;
};

const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const calculateYAxisRange = (values: number[]) => {
  const validValues = values.filter(v => v > 0 && isFinite(v));
  
  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }
  
  const minValue = Math.min(...validValues);
  const maxValue = Math.max(...validValues);
  const range = maxValue - minValue;
  
  return {
    min: Math.max(0, Math.floor(minValue - range * 0.05)),
    max: Math.ceil(maxValue + range * 0.05)
  };
};


const MainDiningChart = React.memo<MainDiningChartProps>(({ 
  dashboardData, 
  selectedGroup, 
  onGroupSelect,
  onMonthlyTrendClick,
  isFiltersApplying,
  filters
}) => {
  const { t } = useTranslation();
  
  const colorPalette = {
    inStoreColor: '#F97F76',
    digitalColor: '#474646',
    emphasisColor: '#F52314'
  };

  
  const isLaptop = window.innerWidth <= 1600;
  const isIPad = window.innerWidth <= 1024;
  const isIPhone = window.innerWidth <= 480;

  
  const { chartData, diningOptionsData } = useMemo(() => {
    if (!dashboardData) return { chartData: null, diningOptionsData: null };

    const chartData = getChartData(dashboardData, 'diningGroup', filters);
    
    const optionsData = dashboardData?.chartData?.diningOptions || [];
    
    
    const optionsByGroup: { [key: string]: any[] } = {};
    
    if (optionsData && optionsData.length > 0) {
      optionsData.forEach((item: any) => {
        const group = item.dining_group;
        if (!optionsByGroup[group]) {
          optionsByGroup[group] = [];
        }
        optionsByGroup[group].push(item);
      });
    }
    
    const diningOptionsData = {
      optionsByGroup: optionsByGroup,
      allOptions: optionsData || []
    };

    return { chartData, diningOptionsData };
  }, [dashboardData, filters]);

  
  const groupColorMapping = useMemo(() => {
    if (!chartData) return {};
    
    const mapping: { [key: string]: string } = {};
    const colors = ['#F97F76', '#474646', '#4285F4', '#34A853', '#FBBC05', '#EA4335'];
    
    chartData.forEach((group: any, index: number) => {
      mapping[group.dining_group] = colors[index % colors.length];
    });
    
    return mapping;
  }, [chartData]);

  
  const recalculatedData = useMemo(() => {
    if (!diningOptionsData || !diningOptionsData.optionsByGroup) return [];

    const { optionsByGroup, allOptions } = diningOptionsData;
    
    
    let barData = selectedGroup && optionsByGroup[selectedGroup] 
      ? optionsByGroup[selectedGroup]
      : allOptions;
    
    
    barData = [...barData].sort((a: any, b: any) => 
      extractNumber(b.net_sales) - extractNumber(a.net_sales)
    );

    
    const targetOptions = selectedGroup && optionsByGroup[selectedGroup] 
      ? optionsByGroup[selectedGroup]
      : allOptions;
    
    const totalSales = targetOptions.reduce((sum: number, option: any) => 
      sum + extractNumber(option.net_sales), 0
    );
    
    
    return barData.map((option: any) => {
      const optionSales = extractNumber(option.net_sales);
      const recalculatedPercentage = totalSales > 0 ? (optionSales / totalSales) * 100 : 0;
      return { ...option, recalculated_percentage: recalculatedPercentage };
    });
  }, [diningOptionsData, selectedGroup]);

  
  const chartOption = useMemo(() => {
    if (!chartData || chartData.length === 0) return {};
    
    const pieData = chartData.map((group: any) => ({
      name: group.dining_group,
      value: extractNumber(group.net_sales),
      percentage: extractPercentage(group.percentage),
      orderCount: group.order_count
    }));
    
    const categories = recalculatedData.map((item: any) => item.cleaned_dining_option);
    const values = recalculatedData.map((item: any) => extractNumber(item.net_sales));
    const percentages = recalculatedData.map((item: any) => item.recalculated_percentage || 0);
    const orderCounts = recalculatedData.map((item: any) => item.order_count);
    
    
    const itemGroupMapping = recalculatedData.map((item: any) => {
      return item.dining_group;
    });

    return {
      grid: [
        {
          left: '2%',
          right: '58%',
          top: isIPhone ? '3%' : (isLaptop ? '6%' : '8%'),
          bottom: isLaptop ? '6%' : '8%',
          containLabel: false
        },
        {
          left: '48%',
          right: '8%',
          top: isIPhone ? '2%' : (isLaptop ? '6%' : '8%'),
          bottom: isIPhone ? '10%' : (isLaptop ? '6%' : '8%'),
          containLabel: true,
          show: false
        }
      ],
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          if (params.seriesType === 'pie') {
            let borderColor = groupColorMapping[params.name] || '#474646';
            
            const dataItem = pieData.find((item: any) => item.name === params.name);
            
            return `<div style="padding: 3px; border-left: 4px solid ${borderColor};">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${params.name}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.sales')}:</span>
                <span style="font-weight: bold;">$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.orders')}:</span>
                <span style="font-weight: bold;">${dataItem?.orderCount || 0}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('diningChart.percentage')}:</span>
                <span style="font-weight: bold;">${dataItem?.percentage.toFixed(2) || 0}%</span>
              </div>
            </div>`;
          }
          
          if (params.seriesType === 'bar') {
            const idx = params.dataIndex;
            const groupType = itemGroupMapping[idx];
            const borderColor = groupColorMapping[groupType] || '#474646';
            const averageTicket = orderCounts[idx] > 0 ? values[idx] / orderCounts[idx] : 0;
            
            return `<div style="padding: 3px; border-left: 4px solid ${borderColor};">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${categories[idx]}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.group')}:</span>
                <span style="font-weight: bold;">${groupType}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.sales')}:</span>
                <span style="font-weight: bold;">$${values[idx].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.orders')}:</span>
                <span style="font-weight: bold;">${orderCounts[idx]}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
                <span style="margin-right: 15px;">${t('diningChart.averageTicket')}:</span>
                <span style="font-weight: bold;">$${averageTicket.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('diningChart.percentage')}:</span>
                <span style="font-weight: bold;">${percentages[idx].toFixed(2)}%</span>
              </div>
            </div>`;
          }
          
          return '';
        }
      },
      series: [
        {
          name: '다이닝 그룹',
          type: 'pie',
          radius: isIPhone ? '45%' : (isIPad ? '55%' : (isLaptop ? '55%' : '70%')),
          center: ['23%', isIPhone ? '45%' : '50%'],
          avoidLabelOverlap: true,
          selectedMode: 'single',
          itemStyle: {
            borderWidth: isLaptop ? 1.5 : 2,
            borderColor: '#fff'
          },
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              const item = pieData.find((d: any) => d.name === params.name);
              if (item) {
                return `${params.name}\n${item.percentage.toFixed(1)}%`;
              }
              return params.name;
            },
            fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 14)),
            fontWeight: 'bold'
          },
          labelLine: {
            show: false
          },
          data: pieData.map((item: any, index: number) => ({
            name: item.name,
            value: item.value,
            itemStyle: {
              color: groupColorMapping[item.name] || '#474646',
              opacity: selectedGroup && selectedGroup !== item.name ? 0.5 : 1
            },
            label: {
              color: index % 2 === 0 ? '#000' : '#fff' 
            }
          }))
        },
        {
          name: '다이닝 옵션',
          type: 'bar',
          barWidth: isIPhone ? '50%' : (isLaptop ? '60%' : '70%'),
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: values.map((value: number, index: number) => ({
            value: value,
            itemStyle: {
              color: groupColorMapping[itemGroupMapping[index]] || '#474646'
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: function(params: any) {
              const idx = params.dataIndex;
              const value = params.value;
              const maxValue = Math.max(...values);
              const formattedValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
              const percentage = percentages[idx].toFixed(1);
              
              
              const shouldBreakLine = (value / maxValue) > 0.6;    
              
              if (shouldBreakLine) {
                return `${formattedValue}\n(${percentage}%)`;
              } else {
                return `${formattedValue} (${percentage}%)`;
              }
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 9 : (isIPad ? 10 : (isLaptop ? 10 : 11)),
            color: '#333'
          }
        }
      ],
      xAxis: [
        { type: 'value', gridIndex: 0, show: false },
        { 
          type: 'value', 
          gridIndex: 1,
          show: false
        }
      ],
      yAxis: [
        { type: 'category', gridIndex: 0, show: false },
        { 
          type: 'category', 
          gridIndex: 1, 
          data: categories,
          axisLabel: {
            interval: 0,
            formatter: function(value: string) {
              if (value.length > 10) {
                const words = value.split(' ');
                if (words.length > 1) {
                  const mid = Math.ceil(words.length / 2);
                  return words.slice(0, mid).join(' ') + '\n' + words.slice(mid).join(' ');
                } else {
                  return value.substring(0, 10) + '\n' + value.substring(10);
                }
              }
              return value;
            },
            fontWeight: 'bold',
            fontSize: isLaptop ? 10 : 12,
            color: '#333',
            lineHeight: isLaptop ? 10 : 12
          }
        }
      ]
    };
  }, [chartData, recalculatedData, selectedGroup, diningOptionsData, t, isLaptop]);

  
  const handleChartEvents = useMemo(() => ({
    'click': function(params: any) {
      if (params.seriesType === 'pie') {
        if (params.name === selectedGroup) {
          onGroupSelect(null);
        } else {
          onGroupSelect(params.name);
        }
      }
    }
  }), [selectedGroup, onGroupSelect]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="dining-chart-container">
        <div className="dining-error">
          <div className="dining-error-text">No chart data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dining-chart-container">
      <div className="dining-navigation">
        <button 
          className="dining-monthly-trend-button" 
          onClick={onMonthlyTrendClick}
          disabled={isFiltersApplying}
          style={{
            fontSize: isLaptop ? '11px' : '12px',
            padding: isLaptop ? '5px 5px' : '6px 12px'
          }}
        >
          {t('diningChart.viewMonthlyTrend')}
        </button>
      </div>
      {selectedGroup && (
        <div className="dining-filter-info">
          <button 
            className="dining-reset-button" 
            onClick={() => onGroupSelect(null)}
            style={{
              fontSize: isLaptop ? '11px' : '13px',
              padding: isLaptop ? '4px 12px' : '5px 15px'
            }}
          >
            {t('diningChart.viewAll')}
          </button>
        </div>
      )}
      <ReactECharts 
        option={chartOption} 
        style={{ height: isIPhone ? '320px' : (isIPad ? '420px' : (isLaptop ? '380px' : '460px')), width: '100%' }}
        className="dining-echarts"
        opts={{ renderer: 'canvas' }}
        onEvents={handleChartEvents}
      />
    </div>
  );
});


const MonthlyTrendModal = React.memo<MonthlyTrendModalProps>(({ 
  show,
  onClose,
  salesforceId,
  modalPosition 
}) => {
  const { t } = useTranslation();
  const { isFiltersApplying } = useFilters();
  const { isiPhone } = useResponsive();
  const isPhoneScreen = window.innerWidth <= 480;

  
  const [trendView, setTrendView] = useState<'groups' | 'options'>('groups');
  const [selectedTrendGroup, setSelectedTrendGroup] = useState<string | null>(null);

  
  const { 
    data: monthlyTrendData, 
    isLoading: trendLoading, 
    error: trendError 
  } = useDiningGroupMonthlyTrendQuery(salesforceId || '', show && !!salesforceId);

  const { 
    data: optionsMonthlyData, 
    isLoading: optionsLoading 
  } = useDiningOptionsMonthlyTrendQuery(
    salesforceId || '', 
    selectedTrendGroup || '', 
    show && !!salesforceId && !!selectedTrendGroup && trendView === 'options'
  );
  
  const groupChartRef = useRef<any>(null);
  
  const colorPalette = {
    inStoreColor: '#F97F76',
    digitalColor: '#474646',
    emphasisColor: '#F52314'
  };

  const optionColors = [
    '#F97F76', '#474646', '#4285F4', '#34A853', '#FBBC05', '#EA4335',
    '#9C27B0', '#FF9800', '#795548', '#607D8B', '#E91E63', '#00BCD4',
    '#8BC34A', '#FFC107', '#673AB7', '#009688'
  ];

  
  const isLoading = trendLoading || optionsLoading;

  
  const displayError = trendError ? 
    (trendError as any)?.message || t('diningChart.monthlyTrendError') : 
    null;

  const updateGroupLineStyles = (selected: {[key: string]: boolean}) => {
    if (!groupChartRef.current) return;
    
    const echartInstance = groupChartRef.current.getEchartsInstance();
    const currentOption = echartInstance.getOption();
    
    const inStoreActive = selected['In-Store Order'];
    const digitalActive = selected['Digital Order'];
    
    const newSeries = currentOption.series.map((series: any, index: number) => {
      if (series.name === 'In-Store Order') {
        return {
          ...series,
          lineStyle: {
            ...series.lineStyle,
            width: 2,
            type: (inStoreActive && digitalActive) ? 'dashed' : 'solid'
          }
        };
      } else if (series.name === 'Digital Order') {
        return {
          ...series,
          lineStyle: {
            ...series.lineStyle,
            width: (inStoreActive && digitalActive) ? 4 : 2
          }
        };
      }
      return series;
    });
    
    echartInstance.setOption({
      series: newSeries
    });
  };

  const getGroupChartEvents = () => {
    return {
      'legendselectchanged': function(params: any) {
        updateGroupLineStyles(params.selected);
      }
    };
  };

  const getGroupTrendOption = () => {
    if (!monthlyTrendData || monthlyTrendData.length === 0) {
      return {};
    }

    const months = monthlyTrendData.map((item: MonthlyDiningGroupData) => formatMonthForDisplay(item.year_month));
    const allValues: number[] = [];

    const inStoreData = monthlyTrendData.map((monthData: MonthlyDiningGroupData) => {
      const value = monthData.groups['In-Store Order'] ? extractNumber(monthData.groups['In-Store Order'].net_sales) : 0;
      if (value > 0) allValues.push(value);
      return value;
    });

    const digitalData = monthlyTrendData.map((monthData: MonthlyDiningGroupData) => {
      const value = monthData.groups['Digital Order'] ? extractNumber(monthData.groups['Digital Order'].net_sales) : 0;
      if (value > 0) allValues.push(value);
      return value;
    });

    const totalValues = inStoreData.map((inStore, index) => inStore + digitalData[index]);
    allValues.push(...totalValues);

    const yAxisRange = calculateYAxisRange(allValues);

    
    const isLaptop = window.innerWidth <= 1600;
    const isIPad = window.innerWidth <= 1024;
    const isIPhone = window.innerWidth <= 480;

    return {
      grid: {
        left: '4%',
        right: '4%',
        top: isIPhone ? '30%' : (isLaptop ? '22%' : '25%'),
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 10 : 11
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: 6px; font-size: ${isLaptop ? '11px' : '12px'};">${monthStr}</div>`;
          
          const inStoreValue = params.find((p: any) => p.seriesName === 'In-Store Order')?.value || 0;
          const digitalValue = params.find((p: any) => p.seriesName === 'Digital Order')?.value || 0;
          const totalValue = inStoreValue + digitalValue;
          
          html += `<div style="display: flex; justify-content: space-between; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #eee;">
            <span style="font-weight: bold; color: #474646;">${t('diningChart.total')}:</span>
            <span style="font-weight: bold; font-size: ${isLaptop ? '11px' : '12px'}; color: #474646;">$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>`;
          
          params.forEach((param: any) => {
            const percentage = totalValue > 0 ? ((param.value / totalValue) * 100).toFixed(1) : '0.0';
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: ${isLaptop ? '10px' : '11px'}">
                $${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                <span style="color: #999; font-size: ${isLaptop ? '9px' : '10px'}">(${percentage}%)</span>
              </span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: ['In-Store Order', 'Digital Order'],
        top: isLaptop ? '4%' : '6%',
        textStyle: {
          fontSize: isLaptop ? 11 : 12,
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 12 : 15,
        selector: false,
        itemStyle: {
          borderWidth: 0
        }
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('diningChart.chartHintStacked'),
            fontSize: isLaptop ? 9 : 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isLaptop ? 4 : 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('diningChart.yAxisMonthlySales'),
        min: yAxisRange.min,
        scale: true,
        nameTextStyle: {
          fontSize: isLaptop ? 10 : 11,
          fontWeight: 'bold'
        },
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: [
        {
          name: 'In-Store Order',
          type: 'line',
          stack: 'total',
          data: inStoreData,
          lineStyle: {
            width: isLaptop ? 1.5 : 2,
            color: '#F97F76',
            type: 'dashed'
          },
          itemStyle: {
            color: '#F97F76'
          },
          areaStyle: {
            color: 'rgba(249, 127, 118, 0.6)'
          },
          symbol: 'circle',
          symbolSize: isLaptop ? 5 : 6
        },
        {
          name: 'Digital Order',
          type: 'line',
          stack: 'total',
          data: digitalData,
          lineStyle: {
            width: isLaptop ? 3 : 4,
            color: '#474646'
          },
          itemStyle: {
            color: '#474646'
          },
          areaStyle: {
            color: 'rgba(71, 70, 70, 0.6)'
          },
          symbol: 'circle',
          symbolSize: isLaptop ? 5 : 6
        }
      ]
    };
  };

  const getOptionsTrendOption = () => {
    if (!optionsMonthlyData || optionsMonthlyData.monthlyData.length === 0) {
      return {};
    }

    const months = optionsMonthlyData.monthlyData.map((item: MonthlyDiningOptionsData) => formatMonthForDisplay(item.year_month));
    const allValues: number[] = [];

    const series = optionsMonthlyData.activeOptions.map((option: string, index: number) => {
      const optionData = optionsMonthlyData.monthlyData.map((monthData: MonthlyDiningOptionsData) => {
        const optionInfo = monthData.options[option];
        const value = optionInfo ? extractNumber(optionInfo.net_sales) : 0;
        if (value > 0) allValues.push(value);
        return value;
      });

      return {
        name: option,
        type: 'line',
        data: optionData,
        lineStyle: {
          width: 2.5,
          color: optionColors[index % optionColors.length]
        },
        itemStyle: {
          color: optionColors[index % optionColors.length]
        },
        symbol: 'circle',
        symbolSize: 5,
        smooth: false
      };
    });

    const yAxisRange = calculateYAxisRange(allValues);

    return {
      grid: {
        left: '4%',
        right: '4%',
        top: (window.innerWidth <= 480) ? '30%' : '20%',
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: 11
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: 6px; font-size: 12px;">${monthStr}</div>`;
          
          const validParams = params.filter((param: any) => param.value > 0);
          
          validParams.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: 11px;">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: optionsMonthlyData.activeOptions,
        top: '6%',
        left: 'center',
        width: '90%',
        textStyle: {
          fontSize: 11,
          fontWeight: 'bold'
        },
        itemGap: 8,
        itemWidth: 12,
        itemHeight: 8,
        orient: 'horizontal',
        selector: false
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('diningChart.chartHintOptions'),
            fontSize: 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('diningChart.yAxisMonthlySales'),
        min: yAxisRange.min,
        scale: true,
        nameTextStyle: {
          fontSize: 11,
          fontWeight: 'bold'
        },
        axisLabel: {
          fontSize: 10,
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: series
    };
  };

  if (!show) return null;

  return (
    <div className="dining-trend-overlay" onClick={onClose}>
      <div 
        className="dining-trend-modal" 
        onClick={(e) => e.stopPropagation()}
        style={isPhoneScreen ? {
          position: 'fixed',
          left: '2.5%',
          top: `${modalPosition.y}px`,
          width: '95%',
          height: '500px',
          margin: '0',
          transform: 'none'
        } : {
          position: 'fixed',
          left: '50%',
          top: `${modalPosition.y}px`,
          transform: 'translateX(-50%)'
        }}
      >
        <div className="dining-trend-modal-header">
          <h3 className="dining-trend-modal-title">
            {trendView === 'groups' 
              ? t('diningChart.modalGroupTitle')
              : t('diningChart.modalOptionsTitle', { group: selectedTrendGroup })}
          </h3>
          <div className="dining-trend-modal-subtitle">
            {trendView === 'groups' 
              ? t('diningChart.modalGroupSubtitle')
              : t('diningChart.modalOptionsSubtitle')}
          </div>
          
          {trendView === 'groups' && (
            <div className="dining-trend-modal-nav">
              <div className="dining-option-buttons">
                <button 
                  className="dining-option-button in-store"
                  onClick={() => {
                    setSelectedTrendGroup('In-Store Order');
                    setTrendView('options');
                  }}
                  disabled={isLoading || isFiltersApplying}
                >
                  {t('diningChart.viewInStoreOptions')}
                </button>
                <button 
                  className="dining-option-button digital"
                  onClick={() => {
                    setSelectedTrendGroup('Digital Order');
                    setTrendView('options');
                  }}
                  disabled={isLoading || isFiltersApplying}
                >
                  {t('diningChart.viewDigitalOptions')}
                </button>
              </div>
            </div>
          )}
          
          {trendView === 'options' && (
            <div className="dining-trend-modal-nav">
              <button 
                className="dining-trend-back-button" 
                onClick={() => {
                  setTrendView('groups');
                  setSelectedTrendGroup(null);
                }}
              >
                {t('diningChart.backToGroups')}
              </button>
            </div>
          )}
          
          <button className="dining-trend-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="dining-trend-modal-content">
          {isLoading ? (
            <div className="dining-trend-loading">
              <div className="dining-trend-loader"></div>
              <div>{t('diningChart.loadingMonthlyTrend')}</div>
            </div>
          ) : displayError ? (
            <div className="dining-trend-error">
              <div className="dining-trend-error-icon">⚠️</div>
              <div>{displayError}</div>
            </div>
          ) : (() => {
            
            if (trendView === 'groups') {
              if (monthlyTrendData && monthlyTrendData.length > 0) {
                return (
                  <ReactECharts 
                    ref={groupChartRef}
                    option={getGroupTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="dining-trend-chart"
                    opts={{renderer: 'canvas'}}
                    onEvents={getGroupChartEvents()}
                    key="group-trend-chart"
                  />
                );
              } else {
                return (
                  <div className="dining-trend-empty">
                    <div>{t('diningChart.noGroupTrendData')}</div>
                  </div>
                );
              }
            } else if (trendView === 'options') {
              if (optionsMonthlyData && optionsMonthlyData.monthlyData && optionsMonthlyData.monthlyData.length > 0) {
                return (
                  <ReactECharts 
                    option={getOptionsTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="dining-trend-chart"
                    opts={{renderer: 'canvas'}}
                    key="options-trend-chart"
                  />
                );
              } else {
                return (
                  <div className="dining-trend-empty">
                    <div>{t('diningChart.noOptionsTrendData')}</div>
                  </div>
                );
              }
            } else {
              return (
                <div className="dining-trend-empty">
                  <div>{t('diningChart.noTrendData')}</div>
                </div>
              );
            }
          })()}
        </div>
      </div>
    </div>
  );
});


const DiningGroupChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { 
    isFiltersApplying,
    dayOfWeek,
    time,
    diningBehavior 
  } = useFilters();

  
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  
  
  const [showMonthlyTrend, setShowMonthlyTrend] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ y: 0 });

  
  const filters = useMemo(() => ({
    dayOfWeek: (!dayOfWeek.allDays && dayOfWeek.selectedDays.length > 0) ? dayOfWeek.selectedDays : undefined,
    hour: (!time.allHours && time.selectedHours.length > 0) ? time.selectedHours : undefined,
    behavior: (!diningBehavior.allBehaviors && diningBehavior.selectedBehaviors.length > 0) ? diningBehavior.selectedBehaviors : undefined,
    diningGroup: undefined,
    menuGroup: undefined
  }), [dayOfWeek, time, diningBehavior]);

  
  const handleGroupSelect = useCallback((group: string | null) => {
    setSelectedGroup(group);
  }, []);

  const handleMonthlyTrendClick = useCallback((event: React.MouseEvent) => {
    const clickY = event.clientY;
    const modalHeight = 650;
    const padding = 20;
    const screenHeight = window.innerHeight;
    
    let finalY = clickY;
    if (clickY + modalHeight + padding > screenHeight) {
      finalY = screenHeight - modalHeight - padding;
    }
    if (finalY < padding) {
      finalY = padding;
    }
    
    setModalPosition({ y: finalY });
    setShowMonthlyTrend(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowMonthlyTrend(false);
  }, []);

  
  const isLaptopRender = window.innerWidth <= 1600;
  const isIPadRender = window.innerWidth <= 1024;
  const isIPhoneRender = window.innerWidth <= 480;

  if (loading || isFiltersApplying) {
    return (
      <div className="dining-group-chart">
        <div className="dining-title-band">
          <h2 style={{
            fontSize: isIPhoneRender ? '14px' : (isIPadRender ? '15px' : (isLaptopRender ? '16px' : '18px')),
            padding: isIPhoneRender ? '6px 15px' : (isIPadRender ? '7px 18px' : (isLaptopRender ? '8px 20px' : '10px 25px'))
          }}>
            {t('diningChart.title')}
          </h2>
        </div>
        <div className="dining-chart-container">
          <div className="dining-loading">
            <div className="dining-loading-spinner"></div>
            <div className="dining-loading-text">
              {isFiltersApplying ? t('diningChart.filterApplying') : t('diningChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dining-group-chart">
        <div className="dining-title-band">
          <h2 style={{
            fontSize: isIPhoneRender ? '14px' : (isIPadRender ? '15px' : (isLaptopRender ? '16px' : '18px')),
            padding: isIPhoneRender ? '6px 15px' : (isIPadRender ? '7px 18px' : (isLaptopRender ? '8px 20px' : '10px 25px'))
          }}>
            {t('diningChart.title')}
          </h2>
        </div>
        <div className="dining-chart-container">
          <div className="dining-error">
            <div className="dining-error-icon">⚠️</div>
            <div className="dining-error-text">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dining-group-chart">
        {isFiltersApplying && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>{t('diningChart.filterApplying')}</span>
          </div>
        )}
        
        <div className="dining-title-band">
          <h2 style={{
            fontSize: isIPhoneRender ? '14px' : (isIPadRender ? '15px' : (isLaptopRender ? '16px' : '18px')),
            padding: isIPhoneRender ? '6px 15px' : (isIPadRender ? '7px 18px' : (isLaptopRender ? '8px 20px' : '10px 25px'))
          }}>
            {t('diningChart.title')}
          </h2>
        </div>
        
        <MainDiningChart
          dashboardData={dashboardData}
          selectedGroup={selectedGroup}
          onGroupSelect={handleGroupSelect}
          onMonthlyTrendClick={handleMonthlyTrendClick}
          isFiltersApplying={isFiltersApplying}
          filters={filters}
        />
      </div>

      <MonthlyTrendModal
        show={showMonthlyTrend}
        onClose={handleCloseModal}
        salesforceId={salesforceId}
        modalPosition={modalPosition}
        
      />
    </>
  );
};

export default DiningGroupChart;